/**
 * Remembering a Postgres-backed table's view between visits: its sort, page
 * size, search, Filters box and where its walk had got to. The Directory began
 * this; the company's Teams, a Team's members and a member's Teams share it.
 *
 * A remembered position is meaningless without the search, filters and sort
 * that produced it (a cursor belongs to one listing and the endpoint refuses it
 * in any other), so they always travel together and are restored together.
 */

import { CursorWalk, restartWalk } from '@/utils/cursorWalk';
import { PanelFilterValue, PanelFilters, normalizePanelFilters } from '@/utils/panelFilters';

export interface ListViewOptions {
	itemsPerPage: number;
	sortBy: string[];
	sortDesc: boolean[];
}

/** What a table stores on the way out. The search travels inside `filters`, as the Directory's does. */
export interface RememberedListView {
	// The cursor that produced the page being left, so coming back lands on it. Null on the first page.
	cursor: string | null;
	// That page's number, so the footer still counts its rows from the right place ("26-50").
	page: number;
	itemsPerPage: number;
	sortBy: string[];
	sortDesc: boolean[];
	filters: Record<string, PanelFilterValue>;
}

/** The same view coming back out of the store, where nothing is trusted. */
export interface StoredListView {
	cursor?: string | null;
	page?: number | null;
	itemsPerPage?: number;
	sortBy?: string[];
	sortDesc?: boolean[];
	filters?: Record<string, unknown> | null;
}

export interface HydratedListView<T extends PanelFilters> {
	options: ListViewOptions;
	// Both null when no filters were stored, so the table leaves its own alone.
	searchQuery: string | null;
	filters: T | null;
	// Restored only with the filters that produced it. Its Back button starts
	// the walk over: the stack of cursors behind a page is not stored.
	walk: CursorWalk;
}

/** A page number from anywhere: a whole number of at least 1, or 1. */
function wholePageNumber(value: unknown): number {
	const page: number = Math.floor(Number(value));
	return Number.isFinite(page) && page >= 1 ? page : 1;
}

export function rememberListView(
	options: Partial<ListViewOptions> | null | undefined,
	searchQuery: string | null | undefined,
	filters: PanelFilters | null | undefined,
	cursor: string | null | undefined,
	page: number | null | undefined,
): RememberedListView {
	const o: Partial<ListViewOptions> = options || {};
	return {
		cursor: cursor || null,
		page: cursor ? wholePageNumber(page) : 1,
		itemsPerPage: Number(o.itemsPerPage) || 0,
		sortBy: Array.isArray(o.sortBy) ? [...o.sortBy] : [],
		sortDesc: Array.isArray(o.sortDesc) ? [...o.sortDesc] : [],
		filters: {
			...(filters || {}),
			searchQuery: searchQuery || '',
		},
	};
}

export interface HydrateListViewRules {
	// Whether the endpoint can sort by this header. Vuetify's third header click
	// leaves `sortBy: []`, and restoring a sort the request cannot make would
	// show an arrow the rows do not follow.
	isSortable: (header: string) => boolean;
	// The page sizes the footer offers. A size outside them is not restored.
	itemsPerPageOptions: number[];
}

export function hydrateListView<T extends PanelFilters>(
	stored: StoredListView | null | undefined,
	defaults: ListViewOptions,
	resting: T,
	rules: HydrateListViewRules,
): HydratedListView<T> {
	const options: ListViewOptions = {
		itemsPerPage: defaults.itemsPerPage,
		sortBy: [...defaults.sortBy],
		sortDesc: [...defaults.sortDesc],
	};
	if (!stored) return { options, searchQuery: null, filters: null, walk: restartWalk() };

	const itemsPerPage: number = Math.floor(Number(stored.itemsPerPage));
	if (rules.itemsPerPageOptions.includes(itemsPerPage)) options.itemsPerPage = itemsPerPage;

	const sortHeader: string | undefined = Array.isArray(stored.sortBy) ? stored.sortBy[0] : undefined;
	if (sortHeader && rules.isSortable(sortHeader)) {
		options.sortBy = [sortHeader];
		options.sortDesc = [Array.isArray(stored.sortDesc) && stored.sortDesc[0] === true];
	}

	const storedFilters: Record<string, unknown> | null =
		stored.filters && typeof stored.filters === 'object' ? stored.filters : null;
	if (!storedFilters) return { options, searchQuery: null, filters: null, walk: restartWalk() };

	const cursor: string | null = typeof stored.cursor === 'string' && stored.cursor ? stored.cursor : null;
	return {
		options,
		searchQuery: typeof storedFilters.searchQuery === 'string' ? storedFilters.searchQuery : '',
		filters: normalizePanelFilters(resting, storedFilters as Partial<Record<keyof T, unknown>>),
		walk: { ...restartWalk(), cursor, startPage: cursor ? wholePageNumber(stored.page) : 1 },
	};
}
